<template>
    <div class="outgoing-call-popup-container">
        <div class="call-popup">
            <div
                v-if="isRinging && !recipientDeclined"
                class="call-popup-content d-flex align-items-center justify-content-center flex-column"
            >
                <span class="call-subtitle">Calling</span>
                <p class="caller-name">
                    {{ formatDisplayUsers([outgoingCallNotification?.recipient]) }}
                </p>
                <div class="d-flex justify-content-center align-items-center call-pad">
                    <AvaInitials
                        classes=""
                        :initialsType="AvaInitialsType.Single"
                        :user="outgoingCallNotification?.recipient"
                    />
                </div>
            </div>

            <div v-else class="call-popup-content d-flex align-items-center justify-content-center flex-column">
                <span v-if="recipientDeclined" class="call-subtitlte sorry-text">Call Declined</span>
                <span v-else class="call-subtitlte sorry-text">Sorry we couldn't connect you.</span>
                <div class="d-flex justify-content-center align-items-center call-pad">
                    <span class="fw-semibold ava-initials bg-light">
                        <img src="../../../../../assets/images/chat/call-unasnswered-gray.png" />
                    </span>
                </div>
            </div>

            <!-- Footer with mic, camera and end call (visible for first 30 seconds) -->
            <div class="call-popup-footer w-100" v-if="isRinging && !recipientDeclined">
                <div class="d-flex w-100 justify-content-between align-items-center px-2">
                    <div class="m-1">
                        <span class="cursor-pointer">
                            <img
                                v-if="callControls.audioEnabled"
                                @click="handleAudioChange(false)"
                                src="../../../../../assets/images/chat/mic-on.svg"
                            />
                            <img
                                v-else
                                @click="handleAudioChange(true)"
                                src="../../../../../assets/images/chat/mic-off.svg"
                            />
                        </span>
                        <span class="mx-3 cursor-pointer">
                            <img
                                v-if="callControls.videoEnabled"
                                @click="handleVideoChange(false)"
                                src="../../../../../assets/images/chat/camera-on.svg"
                            />
                            <img
                                v-else
                                @click="handleVideoChange(true)"
                                src="../../../../../assets/images/chat/camera-off.svg"
                            />
                        </span>
                    </div>
                    <button class="btn end-call-btn" @click="handleCallDecline">
                        <img src="../../../../../assets/images/chat/end-call.svg" class="end-btn-icon" />
                    </button>
                </div>
            </div>

            <!-- Footer with Retry and Dismiss (visible after 30 seconds) -->
            <div class="call-popup-footer" v-if="!isRinging || recipientDeclined">
                <div
                    class="call-btn-wrapper call-btn d-flex align-items-center justify-content-center"
                    @click="handleRetry"
                >
                    <span class="call-message retry-text">Retry</span>
                </div>
                <div class="divider"></div>
                <div
                    class="call-btn-wrapper call-btn d-flex align-items-center justify-content-center"
                    @click="handleCallDismiss"
                >
                    <span class="call-message">Dismiss</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, onMounted, onUnmounted, watch, defineComponent } from "vue";
import { formatDisplayUsers } from "../../../../../common/vue-helpers/modifiers/format-message";
import eventBus from "../../../../../utilities/eventBus";
import { AvaInitialsType } from "../../../../../enums/ConversationTypeEnum";
import AvaInitials from "../../../../MessageCenter/components/AvaInitials/AvaInitials.vue";

const props = defineProps({
    outgoingCallNotification: { type: Object, required: true },
    callControls: { type: Object, required: true },
    recipientDeclined: { type: Boolean, required: false }
});

const emits = defineEmits([
    "close-notification",
    "decline-call",
    "accept-call",
    "audio-changed",
    "video-changed",
    "unanswered-call",
    "retry-call"
]);

const isRinging = ref(true);
let callTimeout = null;

const clearCallTimeout = () => {
    if (callTimeout) {
        clearTimeout(callTimeout);
        callTimeout = null;
    }
};

onMounted(() => {
    showOutgoingCallPopup();
});

onUnmounted(() => {
    clearCallTimeout();
});

watch(
    () => props.recipientDeclined,
    (newVal) => {
        if (newVal) {
            clearCallTimeout();
            isRinging.value = false;
        }
    }
);

const showOutgoingCallPopup = () => {
    callTimeout = setTimeout(() => {
        if (!props.recipientDeclined) {
            isRinging.value = false;
            handleCallUnanswered();
        }
    }, 30000); // 30 seconds
};

const handleCallDecline = (close = true) => {
    emits("decline-call", close);
};

const handleCallUnanswered = () => {
    emits("unanswered-call");
};

const handleCallDismiss = () => {
    emits("close-notification");
};

const handleAudioChange = (value) => {
    emits("audio-changed", value);
};

const handleVideoChange = (value) => {
    emits("video-changed", value);
};

const handleRetry = () => {
    emits("retry-call");
    showOutgoingCallPopup();
};
</script>

<style src="./OutgoingCallNotification.scss"></style>
